import { useEffect, useRef, useState } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useMotionTemplate,
} from "framer-motion";
import { FOUNDERS, PROFILE_URL } from "@/data/founders";
import { CardFront } from "@/components/CardFront";
import { CardBack } from "@/components/CardBack";
import { InvalidLink } from "@/components/InvalidLink";
import { SoundToggle } from "@/components/SoundToggle";
import { playFlick } from "@/utils/flickSound";

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

const readPerson = () => {
  const key = (new URLSearchParams(window.location.search).get("person") || "")
    .trim()
    .toLowerCase();
  return Object.prototype.hasOwnProperty.call(FOUNDERS, key) ? key : null;
};

const CardPage = () => {
  const founder = readPerson() ? FOUNDERS[readPerson()] : null;
  const [flipped, setFlipped] = useState(false);
  const touchStart = useRef(null);

  const tiltX = useMotionValue(0);
  const tiltY = useMotionValue(0);
  const spring = { stiffness: 120, damping: 18, mass: 0.6 };
  const rotateX = useSpring(tiltX, spring);
  const rotateY = useSpring(tiltY, spring);
  const glareX = useTransform(rotateY, [-6, 6], [32, 68]);
  const glareY = useTransform(rotateX, [-6, 6], [68, 32]);
  const glare = useMotionTemplate`radial-gradient(circle at ${glareX}% ${glareY}%, rgba(255, 246, 224, 0.09), rgba(255, 246, 224, 0.03) 38%, transparent 62%)`;

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches)
      return undefined;

    let baseline = null;
    const onOrient = (e) => {
      if (e.beta === null || e.gamma === null) return;
      if (!baseline) baseline = { beta: e.beta, gamma: e.gamma };
      tiltX.set(clamp(-(e.beta - baseline.beta) * 0.32, -5, 5));
      tiltY.set(clamp((e.gamma - baseline.gamma) * 0.32, -6, 6));
    };

    const needsPermission =
      typeof DeviceOrientationEvent !== "undefined" &&
      typeof DeviceOrientationEvent.requestPermission === "function";

    if (needsPermission) {
      // iOS requires a user gesture before gyroscope access
      const requestAndBind = () => {
        DeviceOrientationEvent.requestPermission()
          .then((state) => {
            if (state === "granted")
              window.addEventListener("deviceorientation", onOrient);
          })
          .catch(() => {});
        window.removeEventListener("pointerdown", requestAndBind);
      };
      window.addEventListener("pointerdown", requestAndBind);
      return () => {
        window.removeEventListener("pointerdown", requestAndBind);
        window.removeEventListener("deviceorientation", onOrient);
      };
    }

    window.addEventListener("deviceorientation", onOrient);
    return () => window.removeEventListener("deviceorientation", onOrient);
  }, [tiltX, tiltY]);

  if (!founder) return <InvalidLink />;

  const handleTouchStart = (e) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };

  const handleTouchEnd = (e) => {
    if (!touchStart.current || !flipped) return;
    const dx = e.changedTouches[0].clientX - touchStart.current.x;
    const dy = touchStart.current.y - e.changedTouches[0].clientY;
    touchStart.current = null;
    if (dy > 60 && dy > Math.abs(dx)) {
      window.location.href = PROFILE_URL;
    }
  };

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    tiltY.set(((e.clientX - rect.left) / rect.width - 0.5) * 9);
    tiltX.set(-((e.clientY - rect.top) / rect.height - 0.5) * 7);
  };

  const handleMouseLeave = () => {
    tiltX.set(0);
    tiltY.set(0);
  };

  const toggleFlip = () => {
    // Two light taps ≈ the edge of a card catching, then settling
    if (typeof navigator.vibrate === "function") navigator.vibrate([12, 40, 8]);
    playFlick();
    setFlipped((f) => !f);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleFlip();
    }
  };

  return (
    <div
      data-testid="card-scene"
      className="relative flex h-[100dvh] flex-col items-center justify-center overflow-hidden bg-navy-950"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div className="ambient-light" aria-hidden="true" />
      <div className="vignette" aria-hidden="true" />
      <SoundToggle />

      <motion.div
        initial={{ opacity: 0, y: 28, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 flex flex-col items-center"
      >
        <div style={{ perspective: "1600px" }}>
          <motion.div
            data-testid="card-tilt"
            className="relative"
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
          >
            <motion.div
              data-testid="card-flip"
              role="button"
              tabIndex={0}
              aria-pressed={flipped}
              aria-label={
                flipped
                  ? `${founder.name} contact card, activate to flip back`
                  : `ASCEND Finance Group card, activate to reveal ${founder.name}`
              }
              onClick={toggleFlip}
              onKeyDown={handleKeyDown}
              className="relative cursor-pointer rounded-[22px] outline-none focus-visible:ring-2 focus-visible:ring-gold/60 focus-visible:ring-offset-4 focus-visible:ring-offset-navy-950"
              style={{
                width: "min(86vw, 350px, 52dvh)",
                aspectRatio: "5 / 7.6",
                transformStyle: "preserve-3d",
              }}
              animate={{ rotateY: flipped ? 180 : 0 }}
              transition={{ duration: 0.8, ease: [0.35, 0, 0.15, 1] }}
            >
              <CardFront active={!flipped} />
              <CardBack founder={founder} active={flipped} />
              <motion.div
                aria-hidden="true"
                data-testid="card-glare"
                className="pointer-events-none absolute inset-0 z-20 rounded-[22px]"
                style={{ background: glare }}
              />
            </motion.div>
          </motion.div>
        </div>

        <motion.a
          href={PROFILE_URL}
          data-testid="view-profile-button"
          initial={false}
          animate={{
            opacity: flipped ? 1 : 0,
            y: flipped ? 0 : 10,
          }}
          transition={{ duration: 0.5, delay: flipped ? 0.35 : 0 }}
          className="mt-9 border-b border-gold/40 pb-1.5 pl-[0.35em] text-[11px] font-medium tracking-[0.35em] text-gold transition-colors duration-300 hover:border-gold hover:text-gold-light"
          style={{ pointerEvents: flipped ? "auto" : "none" }}
          tabIndex={flipped ? 0 : -1}
        >
          VIEW ASCEND PROFILE
        </motion.a>
      </motion.div>
    </div>
  );
};

export default CardPage;
